/**
 * BottomTabBar — primary mobile navigation.
 *
 * Renders a fixed four-tab bar at the bottom of the viewport, mirroring
 * the desktop sidebar links. Sits inside the mobile shell only; desktop
 * keeps the left sidebar.
 *
 * Each tab is a button (not an anchor) so the active state and focus
 * styles match the rest of the in-app navigation, while still triggering
 * `onNavigate(hash)` which delegates to React's hash routing in App.tsx.
 */
import type { ReactElement } from 'react';

interface BottomTabBarProps {
  currentPath: string;
  onNavigate: (hash: string) => void;
  onOpenMore: () => void;
  moreOpen: boolean;
}

interface TabDef {
  hash: string;
  label: string;
  testId: string;
  matcher: (path: string) => boolean;
  icon: (active: boolean) => ReactElement;
}

const HOME_ICON = (active: boolean) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-8.5z"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const QUEUE_ICON = (active: boolean) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect
      x="3"
      y="5"
      width="18"
      height="3.5"
      rx="1"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <rect
      x="3"
      y="10.25"
      width="18"
      height="3.5"
      rx="1"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <rect
      x="3"
      y="15.5"
      width="18"
      height="3.5"
      rx="1"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const ACTIVITY_ICON = (active: boolean) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" fill={active ? 'currentColor' : 'none'} />
    <path d="M12 7v5l3.5 2" stroke={active ? '#ffffff' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </svg>
);

const MORE_ICON = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="6" cy="12" r="1.8" fill="currentColor" />
    <circle cx="12" cy="12" r="1.8" fill="currentColor" />
    <circle cx="18" cy="12" r="1.8" fill="currentColor" />
  </svg>
);

const TABS: TabDef[] = [
  {
    hash: '#/',
    label: 'Home',
    testId: 'tab-home',
    matcher: (p) => p === '' || p === '#/' || p === '#',
    icon: HOME_ICON,
  },
  {
    hash: '#/submissions',
    label: 'Queue',
    testId: 'tab-queue',
    matcher: (p) => p.startsWith('#/submissions'),
    icon: QUEUE_ICON,
  },
  {
    hash: '#/activity',
    label: 'Activity',
    testId: 'tab-activity',
    matcher: (p) => p.startsWith('#/activity'),
    icon: ACTIVITY_ICON,
  },
];

export default function BottomTabBar({
  currentPath,
  onNavigate,
  onOpenMore,
  moreOpen,
}: BottomTabBarProps) {
  return (
    <nav
      className="bottom-tab-bar"
      aria-label="Primary navigation"
      data-testid="bottom-tab-bar"
    >
      {TABS.map((tab) => {
        const active = !moreOpen && tab.matcher(currentPath);
        return (
          <button
            key={tab.hash}
            type="button"
            className={`bottom-tab${active ? ' bottom-tab--active' : ''}`}
            aria-current={active ? 'page' : undefined}
            data-testid={tab.testId}
            onClick={() => onNavigate(tab.hash)}
          >
            <span className="bottom-tab__icon" aria-hidden="true">
              {tab.icon(active)}
            </span>
            <span className="bottom-tab__label">{tab.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`bottom-tab${moreOpen ? ' bottom-tab--active' : ''}`}
        aria-pressed={moreOpen}
        aria-haspopup="dialog"
        data-testid="tab-more"
        onClick={onOpenMore}
      >
        <span className="bottom-tab__icon" aria-hidden="true">
          {MORE_ICON()}
        </span>
        <span className="bottom-tab__label">More</span>
      </button>
    </nav>
  );
}
